import React, {useCallback} from 'react';
import {useIntl} from 'react-intl';

import {Button} from '@views/Account/Plan/_components/ActionButtons';
import {useView} from '@views/Account/Plan/_hooks/useView';
import {AccountApplication} from '@application/Account';
import {useToast} from '@components/_context';
import {emitEventSubscriptionUpdated} from '@views/Account/Plan/MyPlan/MyPlan.controller';
import {useCurrentPlan} from '@views/Account/Plan/_hooks/useCurrentPlan';
import {RecurrentModalities} from '@views/Account/Plan/MyPlan/MyPlan.definition';
import {ModalFrequencySelectDay} from '@views/Account/Plan/MyPlan/ModalFrequencySelectDay';
import {ConfigurationApplication} from '@application/Config';
import {TradeName} from '@domain/model/Config/Configuration';
import {useLoading} from '@views/Account/Plan/_hooks/useLoading';

const isLookiero = ConfigurationApplication.retrieve().tradename === TradeName.LOOKIERO;

const TEXT = {
  BUTTON_CHANGE_RECURRENT_DATE: {id: 'account_yourPlan.planPanel.changeRecurrentDate'},
  TOAST_SUCCESS: {id: 'account.frequency.toast_success_updated'},
  TOAST_ERROR: {id: 'account.frequency.toast_error'},
};

interface ActionChangeDayProps {
  onSuccess: () => Promise<void>;
}

const ActionChangeDay: React.FC<ActionChangeDayProps> = ({onSuccess}) => {
  const {formatMessage} = useIntl();
  const {visible, open, close} = useView();
  const {showError, showSuccess} = useToast();
  const {day, modality} = useCurrentPlan();
  const {loading, startLoading, stopLoading} = useLoading();

  const handleSubmit = useCallback(
    async (value: number) => {
      try {
        startLoading();
        await AccountApplication.setSubscription({lkDay: value, modality});
        await emitEventSubscriptionUpdated(modality, modality, {
          previousFrequencyDay: day,
          currentFrequencyDay: value,
        });
        showSuccess({message: formatMessage(TEXT.TOAST_SUCCESS)});
        await onSuccess();
        close();
      } catch {
        showError({message: formatMessage(TEXT.TOAST_ERROR)});
      } finally {
        stopLoading();
      }
    },
    [modality, day, formatMessage, showSuccess, showError, onSuccess, close, startLoading, stopLoading],
  );

  return (
    <>
      <Button onPress={open}>{formatMessage(TEXT.BUTTON_CHANGE_RECURRENT_DATE)}</Button>
      <ModalFrequencySelectDay
        modality={modality as RecurrentModalities}
        day={day}
        isVisible={visible}
        onClose={close}
        onSubmit={handleSubmit}
        busy={loading}
      />
    </>
  );
};

const ActionChangeDayWrapper: React.FC<ActionChangeDayProps> = props => {
  const {isOnDemand} = useCurrentPlan();
  return !isOnDemand && isLookiero && <ActionChangeDay {...props} />;
};

export {ActionChangeDayWrapper as ActionChangeDay};
